/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure, Firebase-decoupled helpers for the SOS write + native-permission
 * paths. `useSOSEvent` injects the real `set`/Sentry sinks; tests inject
 * mocks (DI pattern per CLAUDE.md — no firebase plumbing pulled into the
 * root test runner).
 *
 * Critical: error extraction must NEVER go through `JSON.stringify(error)`.
 * Firebase RTDB errors serialise to `{}` (no own-enumerable props), which is
 * exactly why the SOS crashes were invisible in Sentry — the `code`,
 * `message`, and `stack` were thrown away.
 */

type BreadcrumbLevel = "info" | "warning" | "error";

export interface SosBreadcrumb {
  category: string;
  level: BreadcrumbLevel;
  message: string;
  data?: Record<string, unknown>;
}

export interface SosErrorInfo {
  code: string;
  message: string;
  stack: string;
}

/** Extract `code/message/stack` from any thrown value WITHOUT stringifying. */
export function describeError(err: unknown): SosErrorInfo {
  if (err instanceof Error) {
    return {
      code: String((err as { code?: unknown }).code ?? "unknown"),
      message: err.message || String(err),
      stack: err.stack ?? "",
    };
  }
  const anyErr = err as { code?: unknown; message?: unknown } | null;
  return {
    code: String(anyErr?.code ?? "unknown"),
    message:
      typeof anyErr?.message === "string" ? anyErr.message : String(err),
    stack: "",
  };
}

export interface SosWriteSinks {
  /** The actual RTDB write (e.g. `() => set(eventRef, initial)`). */
  setEvent: () => Promise<unknown>;
  addBreadcrumb: (b: SosBreadcrumb) => void;
  captureException: (
    err: unknown,
    ctx: { tags: Record<string, string>; extra: Record<string, unknown> },
  ) => void;
}

export interface SosWriteResult {
  ok: boolean;
  error?: SosErrorInfo;
}

/**
 * Run the SOS RTDB write with start/succeeded/failed breadcrumbs and a
 * full-context Sentry capture on failure. Never throws — the caller's SOS
 * UI must keep working (panel + tel:911) even when the write rejects.
 */
export async function writeSosEvent(
  sinks: SosWriteSinks,
  context: Record<string, unknown> = {},
): Promise<SosWriteResult> {
  sinks.addBreadcrumb({
    category: "sos",
    level: "info",
    message: "sos.rtdb_write_started",
    data: context,
  });
  try {
    await sinks.setEvent();
    sinks.addBreadcrumb({
      category: "sos",
      level: "info",
      message: "sos.rtdb_write_succeeded",
      data: context,
    });
    return { ok: true };
  } catch (err) {
    const info = describeError(err);
    sinks.addBreadcrumb({
      category: "sos",
      level: "error",
      message: "sos.rtdb_write_failed",
      data: { ...context, code: info.code },
    });
    sinks.captureException(err, {
      tags: { area: "sos", feature: "sos", phase: "rtdb_write" },
      extra: {
        ...context,
        code: info.code,
        message: info.message,
        stack: info.stack,
      },
    });
    return { ok: false, error: info };
  }
}

export interface MicPermissionSinks {
  addBreadcrumb: (b: SosBreadcrumb) => void;
}

export function recordMicPermissionRequested(sinks: MicPermissionSinks): void {
  sinks.addBreadcrumb({
    category: "sos",
    level: "info",
    message: "sos.permission_requested",
    data: { kind: "microphone" },
  });
}

export function recordMicPermissionResult(
  sinks: MicPermissionSinks,
  granted: boolean,
  err?: unknown,
): void {
  sinks.addBreadcrumb({
    category: "sos",
    level: granted ? "info" : "warning",
    message: "sos.permission_result",
    data: {
      kind: "microphone",
      result: granted ? "granted" : "denied",
      ...(err ? { code: describeError(err).code } : {}),
    },
  });
}
